import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, message } from 'antd';
import { ArrowLeft, Layers, RefreshCw, X } from 'lucide-react';

import {
  getQuickInference,
  getQuickInferenceSession,
} from '../../../helpers/api/quick-inference/quick-inference';
import type {
  QuickInferenceDetail,
  QuickInferenceSessionDetail,
} from '../../../helpers/api/quick-inference/quick-inference';
import { QuickInferenceViewerCanvas } from './QuickInferenceViewerCanvas';
import { QuickInferenceViewerFileList } from './QuickInferenceViewerFileList';
import { sessionDisplayTitle, truncateFilename } from './quickInferenceUtils';

function QuickInferenceViewerPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();

  const [sessionDetail, setSessionDetail] = useState<QuickInferenceSessionDetail | null>(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [filesPanelOpen, setFilesPanelOpen] = useState(false);
  const filesPanelAutoOpened = useRef(false);

  const [selectedFileId, setSelectedFileId] = useState<string | null>(null);
  const [fileDetail, setFileDetail] = useState<QuickInferenceDetail | null>(null);
  const [fileDetailLoading, setFileDetailLoading] = useState(false);

  const loadSession = useCallback(async () => {
    if (!sessionId) return;
    setSessionError(null);
    try {
      const data = await getQuickInferenceSession(sessionId);
      if (!data) {
        setSessionError('Session not found');
        setSessionDetail(null);
        return;
      }
      setSessionDetail(data);
      setSelectedFileId((prev) => {
        if (prev && data.files.some((f) => f.id === prev)) return prev;
        return data.files.length === 1 ? data.files[0].id : data.files[0]?.id ?? null;
      });
    } catch (err: any) {
      setSessionError(err?.message || 'Failed to load session');
      setSessionDetail(null);
    } finally {
      setSessionLoading(false);
    }
  }, [sessionId]);

  const loadFileDetail = useCallback(async (fileId: string) => {
    setFileDetailLoading(true);
    try {
      const data = await getQuickInference(fileId);
      setFileDetail(data);
    } catch (err: any) {
      setFileDetail(null);
      message.error(err?.message || 'Failed to load file');
    } finally {
      setFileDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  useEffect(() => {
    if (!selectedFileId) {
      setFileDetail(null);
      return;
    }
    loadFileDetail(selectedFileId);
  }, [selectedFileId, loadFileDetail]);

  useEffect(() => {
    if (!sessionId || !sessionDetail) return;
    const active =
      sessionDetail.status === 'pending' ||
      sessionDetail.status === 'processing' ||
      sessionDetail.files?.some((f) => f.status === 'pending');
    if (!active) return;
    const timer = window.setInterval(() => {
      getQuickInferenceSession(sessionId).then((next) => {
        if (next) setSessionDetail(next);
      });
    }, 3000);
    return () => window.clearInterval(timer);
  }, [sessionId, sessionDetail]);

  useEffect(() => {
    if (!selectedFileId) return;
    const fileSummary = sessionDetail?.files?.find((f) => f.id === selectedFileId);
    const stillPending =
      fileDetail?.status === 'pending' || fileSummary?.status === 'pending' || !fileDetail;
    if (!stillPending) return;

    const poll = () => {
      getQuickInference(selectedFileId).then((next) => {
        if (next) setFileDetail(next);
      });
    };
    poll();
    const timer = window.setInterval(poll, 3000);
    return () => window.clearInterval(timer);
  }, [selectedFileId, fileDetail?.status, sessionDetail?.files]);

  const files = sessionDetail?.files ?? [];
  const selectedFileIndex = useMemo(
    () => files.findIndex((f) => f.id === selectedFileId),
    [files, selectedFileId]
  );
  const hasMultipleFiles = files.length > 1;

  useEffect(() => {
    if (!hasMultipleFiles || !sessionDetail) return;
    const isDesktop = window.matchMedia('(min-width: 1024px)').matches;
    if (isDesktop && !filesPanelAutoOpened.current) {
      setFilesPanelOpen(true);
      filesPanelAutoOpened.current = true;
    }
  }, [hasMultipleFiles, sessionDetail]);

  const handleDetailRefresh = useCallback(async () => {
    if (!selectedFileId || !sessionId) return;
    await loadFileDetail(selectedFileId);
    await loadSession();
  }, [selectedFileId, sessionId, loadFileDetail, loadSession]);

  const handleFileSelect = useCallback((fileId: string) => {
    setSelectedFileId(fileId);
    if (hasMultipleFiles) setFilesPanelOpen(false);
  }, [hasMultipleFiles]);

  if (!sessionId) {
    return (
      <div className="p-6">
        <Alert type="error" message="Missing session id" showIcon />
      </div>
    );
  }

  const sessionActive =
    sessionDetail?.status === 'pending' || sessionDetail?.status === 'processing';

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-2 p-2 sm:gap-3 sm:p-3">
        <header className="flex min-h-11 shrink-0 flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-white px-2 py-2 shadow-sm dark:border-gray-800 dark:bg-gray-900 sm:gap-2.5 sm:px-3">
        <button
          type="button"
          onClick={() => navigate('/playground')}
          className="inline-flex h-8 shrink-0 items-center gap-1 rounded-lg px-2 text-xs font-semibold text-blue-600 transition hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
        >
          <ArrowLeft size={14} />
          <span className="hidden sm:inline">Back</span>
        </button>

        <div className="hidden h-4 w-px bg-gray-200 dark:bg-gray-700 sm:block" />

        <button
          type="button"
          onClick={() => hasMultipleFiles && setFilesPanelOpen((o) => !o)}
          disabled={!hasMultipleFiles}
          className={`flex h-8 shrink-0 items-center gap-1.5 rounded-lg border px-2.5 text-xs font-semibold transition-all sm:px-3 ${
            hasMultipleFiles && filesPanelOpen
              ? 'border-blue-600 bg-blue-600 text-white shadow-sm'
              : 'border-gray-200 bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:cursor-default disabled:opacity-70 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400'
          }`}
        >
          <Layers size={12} />
          <span className="hidden min-[400px]:inline">{hasMultipleFiles ? 'Files' : 'File'}</span>
          <span
            className={`min-w-[18px] rounded px-1.5 py-0.5 text-[9px] font-black ${
              hasMultipleFiles && filesPanelOpen
                ? 'bg-white/25 text-white'
                : 'bg-gray-300 text-gray-700 dark:bg-gray-700 dark:text-gray-400'
            }`}
          >
            {files.length || 1}
          </span>
        </button>

        {fileDetail && selectedFileIndex >= 0 ? (
          <>
            <div className="hidden h-4 w-px bg-gray-200 dark:bg-gray-700 md:block" />
            <div className="flex min-w-0 max-w-full flex-1 items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-2 py-1 sm:max-w-none sm:flex-none dark:border-green-800/50 dark:bg-green-900/20">
              <span className="relative flex h-2 w-2 shrink-0">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-green-500" />
              </span>
              <span className="shrink-0 text-[10px] font-bold text-green-700 dark:text-green-400">
                File {selectedFileIndex + 1}
              </span>
              <span
                className="hidden truncate text-[10px] text-green-600/80 sm:inline dark:text-green-400/80"
                title={fileDetail.filename}
              >
                {truncateFilename(fileDetail.filename, 24)}
              </span>
            </div>
          </>
        ) : sessionDetail ? (
          <span className="min-w-0 flex-1 truncate text-xs font-medium text-gray-600 dark:text-gray-400">
            {sessionDisplayTitle(sessionDetail)}
          </span>
        ) : null}

        <div className="hidden flex-1 sm:block" />

        <button
          type="button"
          onClick={loadSession}
          disabled={sessionLoading || fileDetailLoading}
          className="ml-auto flex h-8 shrink-0 items-center gap-1.5 rounded-lg border border-gray-200 bg-gray-100 px-2.5 text-xs font-semibold text-gray-600 transition hover:bg-gray-200 disabled:opacity-50 sm:ml-0 sm:px-3 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400"
        >
          <RefreshCw size={12} className={sessionLoading ? 'animate-spin' : ''} />
          <span className="hidden sm:inline">Refresh</span>
        </button>
        </header>

        {sessionError ? (
          <Alert type="error" showIcon message={sessionError} />
        ) : null}

        {sessionActive ? (
          <div className="shrink-0 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-700 dark:border-blue-900/40 dark:bg-blue-900/20 dark:text-blue-300">
            Inference in progress — results will update automatically.
          </div>
        ) : null}

        <div className="relative min-h-0 flex-1 overflow-hidden rounded-lg border border-gray-200 bg-gray-50 shadow-sm dark:border-gray-700 dark:bg-gray-950">
          <main className="absolute inset-0 flex flex-col overflow-y-auto lg:overflow-hidden">
          {sessionLoading && !sessionDetail ? (
            <div className="flex h-full items-center justify-center">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-blue-500" />
            </div>
          ) : !selectedFileId ? (
            <div className="flex h-full flex-col items-center justify-center gap-4 rounded-lg border border-slate-200 bg-white p-10 shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-gray-200 bg-gray-50 shadow-sm dark:border-gray-700 dark:bg-gray-900">
                <Layers size={22} className="text-blue-500" />
              </div>
              <div className="text-center">
                <p className="text-sm font-semibold text-gray-600 dark:text-gray-300">
                  {hasMultipleFiles ? 'Select a file' : 'No file available'}
                </p>
                {hasMultipleFiles ? (
                  <p className="mt-1 text-xs text-gray-400">
                    Open the Files panel to choose a file from this session
                  </p>
                ) : null}
              </div>
            </div>
          ) : fileDetailLoading && !fileDetail ? (
            <div className="flex h-full min-h-[420px] items-center justify-center rounded-lg border border-slate-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-blue-500" />
            </div>
          ) : fileDetail ? (
            <div className="min-h-0 flex-1">
              <QuickInferenceViewerCanvas detail={fileDetail} onDetailRefresh={handleDetailRefresh} />
            </div>
          ) : (
            <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <Alert type="warning" showIcon message="Could not load file" />
            </div>
          )}
        </main>

        {hasMultipleFiles ? (
          <>
            {filesPanelOpen ? (
              <button
                type="button"
                aria-label="Close files panel"
                className="absolute inset-0 z-10 bg-black/30 lg:hidden"
                onClick={() => setFilesPanelOpen(false)}
              />
            ) : null}
            <aside
              className="absolute bottom-0 left-0 top-0 z-20 flex w-full max-w-[280px] flex-col border-r border-gray-200 bg-white transition-transform duration-200 ease-out dark:border-gray-800 dark:bg-gray-900 sm:max-w-[220px] lg:w-[168px] lg:max-w-none"
              style={{
                boxShadow: filesPanelOpen ? '4px 0 24px rgba(0,0,0,0.10)' : 'none',
                transform: filesPanelOpen ? 'translateX(0)' : 'translateX(-100%)',
              }}
            >
            <div className="flex h-9 shrink-0 items-center justify-between border-b border-gray-200 bg-gray-50 px-3 dark:border-gray-800 dark:bg-gray-800/50">
              <div className="flex items-center gap-2">
                <div className="h-3.5 w-0.5 rounded-full bg-blue-500" />
                <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
                  {files.length} File{files.length === 1 ? '' : 's'}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setFilesPanelOpen(false)}
                className="flex h-6 w-6 items-center justify-center rounded-md text-gray-400 transition hover:bg-gray-200 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200"
              >
                <X size={12} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-2 py-2" style={{ scrollbarWidth: 'thin' }}>
              <QuickInferenceViewerFileList
                files={files}
                selectedFileId={selectedFileId}
                onSelect={handleFileSelect}
              />
            </div>
          </aside>
          </>
        ) : null}
        </div>
      </div>
    </div>
  );
}

export default QuickInferenceViewerPage;
